import React, { useMemo } from "react";
import { useLanguage } from "../../../contexts/LanguageContext";
import { ChevronLeft, ChevronRight } from "lucide-react";
import "./Pagination.css";

export default function Pagination({
  currentPage,
  totalPages,
  onPageChange,
  siblingCount = 1,
}) {
  const { lang } = useLanguage();
  const isAr = lang === "ar";

  const pages = useMemo(() => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const leftSibling = Math.max(currentPage - siblingCount, 2);
    const rightSibling = Math.min(currentPage + siblingCount, totalPages - 1);

    const showLeftDots = leftSibling > 2;
    const showRightDots = rightSibling < totalPages - 1;

    const items = [1];

    if (showLeftDots) {
      items.push("left-dots");
    } else if (leftSibling === 2) {
      items.push(2);
    }

    for (let i = leftSibling; i <= rightSibling; i++) {
      if (!items.includes(i)) items.push(i);
    }

    if (showRightDots) {
      items.push("right-dots");
    } else if (rightSibling === totalPages - 1) {
      items.push(totalPages - 1);
    }

    if (!items.includes(totalPages)) items.push(totalPages);

    return items;
  }, [currentPage, totalPages, siblingCount]);

  if (totalPages <= 1) return null;

  return (
    <nav
      className="pagination-wrapper"
      dir={isAr ? "rtl" : "ltr"}
      aria-label={isAr ? "التنقل بين الصفحات" : "Pagination"}
    >
      <ul className="pagination-list">
        {/* Previous */}
        <li>
          <button
            className="pagination-btn pagination-prev"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            aria-label={isAr ? "الصفحة السابقة" : "Previous page"}
          >
            {isAr ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          </button>
        </li>

        {/* Page numbers */}
        {pages.map((page, idx) => {
          if (page === "left-dots" || page === "right-dots") {
            return (
              <li key={`dots-${idx}`}>
                <span className="pagination-dots">…</span>
              </li>
            );
          }

          return (
            <li key={page}>
              <button
                className={`pagination-btn pagination-num ${
                  page === currentPage ? "active" : ""
                }`}
                onClick={() => onPageChange(page)}
                aria-label={
                  isAr ? `الصفحة ${page}` : `Page ${page}`
                }
                aria-current={page === currentPage ? "page" : undefined}
              >
                {page}
              </button>
            </li>
          );
        })}

        {/* Next */}
        <li>
          <button
            className="pagination-btn pagination-next"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            aria-label={isAr ? "الصفحة التالية" : "Next page"}
          >
            {isAr ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
          </button>
        </li>
      </ul>
    </nav>
  );
}